import React, { useRef, useEffect } from 'react'
import ReactECharts from 'echarts-for-react'

/**
 * Chart renderer component using ECharts.
 * Receives pre-built chart options and renders the chart.
 *
 * @param {Object} props
 * @param {Object} props.options - Pre-built ECharts options
 * @param {number|string} props.height - Chart height
 * @param {number|string} props.width - Chart width
 * @param {boolean} props.clickable - Whether chart elements are clickable
 * @param {Function} props.onChartClick - Callback for chart element clicks
 * @param {Function} props.onDrill - Callback for drill-down actions
 * @param {Object} props.embeddedSizeProps - Size props for embedded rendering
 * @param {boolean} props.chat - Whether rendering in chat context
 */
export const SummaryChart = ({
    options,
    height,
    width,
    clickable = false,
    onChartClick,
    onDrill,
    embeddedSizeProps,
    chat = false,
}) => {
    const chartRef = useRef(null)

    // Handle animated resize when container size changes
    useEffect(() => {
        let resizeTimeout = null

        const resizeObserver = new ResizeObserver(() => {
            // Debounce and use requestAnimationFrame to avoid ResizeObserver loop error
            if (resizeTimeout) cancelAnimationFrame(resizeTimeout)
            resizeTimeout = requestAnimationFrame(() => {
                const chartInstance = chartRef.current?.getEchartsInstance()
                if (chartInstance) {
                    chartInstance.resize({
                        animation: {
                            duration: 225,
                            easing: 'cubicOut',
                        },
                    })
                }
            })
        })

        const container = chartRef.current?.ele
        if (container) {
            resizeObserver.observe(container)
        }

        return () => {
            resizeObserver.disconnect()
            if (resizeTimeout) cancelAnimationFrame(resizeTimeout)
        }
    }, [])
    // Calculate chart style based on context
    const chartStyle = {
        height: embeddedSizeProps
            ? embeddedSizeProps.height
            : height
              ? height
              : chat
                ? 500
                : `${window.innerHeight - 112 - 64}px`,
        width: width || '100%',
        pointerEvents: clickable ? 'all' : 'none',
        margin: embeddedSizeProps || chat ? 0 : '32px',
    }

    // Event handlers for chart interactions
    const onEvents = {}

    if (onChartClick) {
        onEvents.click = (params) => {
            onChartClick(params)
        }
    }

    if (onDrill) {
        onEvents.dblclick = (params) => {
            onDrill(params)
        }
    }

    return (
        <ReactECharts
            ref={chartRef}
            option={options}
            style={chartStyle}
            notMerge={true}
            onEvents={Object.keys(onEvents).length > 0 ? onEvents : undefined}
        />
    )
}
